import { useMemo, useState } from "react";

/**
 * Constants (edit these)
 */
// AI cost calculation
const AI_COST_PER_MINUTE_DKK = 2.55; // AI processing cost per minute of call
const AVERAGE_CALL_LENGTH_MINUTES = 4.0; // Average call duration per call in minutes
const AI_COST_PER_CALL_DKK = AI_COST_PER_MINUTE_DKK * AVERAGE_CALL_LENGTH_MINUTES;

// Other cost components
const SMS_COST_PER_ORDER_DKK = 0.4; // SMS notification cost per order (Twilio: $0.0524 per SMS to DK mobile numbers, converted to DKK) - only for successful orders
const PAYMENT_COST_PER_ORDER_DKK = 0; // Payment processing cost per order - only for successful orders
const INFRA_COST_PER_CALL_DKK = 0.2; // Infrastructure cost per call (applies to all calls, not just orders)

// Calculate variable cost per order based on calls and orders
// Cost per order = (call costs * calls) / orders + (order costs)
const calculateVariableCostPerOrder = (monthlyCalls, monthlyOrders) => {
  if (monthlyOrders <= 0) return Infinity;
  const callCosts = (AI_COST_PER_CALL_DKK + INFRA_COST_PER_CALL_DKK) * monthlyCalls;
  const orderCosts = (SMS_COST_PER_ORDER_DKK + PAYMENT_COST_PER_ORDER_DKK) * monthlyOrders;
  return (callCosts + orderCosts) / monthlyOrders;
};

const TARGET_PROFIT_PER_MONTH_DKK = 2700; // your target profit per restaurant per month
const CUSTOMER_FEE_SAFETY_MARGIN = 0.2; // +20% safety margin applied to customer fee per order

// Optional guardrails (keep or remove)
const MIN_ORDERS_PER_MONTH = 1;
const MAX_ORDERS_PER_MONTH = 100000;
const MIN_CALLS_PER_MONTH = 1;
const MAX_CALLS_PER_MONTH = 1000000;

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const formatDKK = (n) =>
  new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", maximumFractionDigits: 0 }).format(
    isFinite(n) ? n : 0
  );

const formatDKK2 = (n) =>
  new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    isFinite(n) ? n : 0
  );

export default function PricingCalculator() {
  // Inputs
  const [monthlyCalls, setMonthlyCalls] = useState(938); // Total calls per month
  const [monthlyOrders, setMonthlyOrders] = useState(750); // Orders completed per month
  const [customerFeeShare, setCustomerFeeShare] = useState(0.5); // 0..1
  const [manualCustomerFee, setManualCustomerFee] = useState(null); // null or number (DKK)
  const [manualMonthlyFee, setManualMonthlyFee] = useState(null); // null or number (DKK)

  const safeMonthlyCalls = useMemo(
    () => clamp(Math.round(monthlyCalls || 0), MIN_CALLS_PER_MONTH, MAX_CALLS_PER_MONTH),
    [monthlyCalls]
  );
  const safeMonthlyOrders = useMemo(
    () => clamp(Math.round(monthlyOrders || 0), MIN_ORDERS_PER_MONTH, MAX_ORDERS_PER_MONTH),
    [monthlyOrders]
  );
  const safeShare = useMemo(() => clamp(customerFeeShare || 0, 0, 1), [customerFeeShare]);
  
  // Calculate variable cost per order based on calls and orders
  const variableCostPerOrder = useMemo(
    () => calculateVariableCostPerOrder(safeMonthlyCalls, safeMonthlyOrders),
    [safeMonthlyCalls, safeMonthlyOrders]
  );

  const results = useMemo(() => {
    // Required total contribution per month to cover variable costs + hit target profit
    // Matches spreadsheet: Required = TargetProfit + (Orders * VarCostPerOrder)
    const requiredTotalContribution = TARGET_PROFIT_PER_MONTH_DKK + safeMonthlyOrders * variableCostPerOrder;

    let customerContributionMonthly;
    let restaurantSubscriptionMonthly;
    let baseCustomerFeePerOrder;
    let customerFeePerOrder;
    let calculatedShare = safeShare;

    // Reverse calculation: if manual customer fee is set
    if (manualCustomerFee !== null && manualCustomerFee >= 0) {
      // Calculate backwards from customer fee per order
      customerFeePerOrder = manualCustomerFee;
      baseCustomerFeePerOrder = customerFeePerOrder / (1 + CUSTOMER_FEE_SAFETY_MARGIN);
      customerContributionMonthly = baseCustomerFeePerOrder * safeMonthlyOrders;
      restaurantSubscriptionMonthly = Math.max(0, requiredTotalContribution - customerContributionMonthly);
      calculatedShare = clamp(customerContributionMonthly / requiredTotalContribution, 0, 1);
    }
    // Reverse calculation: if manual monthly fee is set
    else if (manualMonthlyFee !== null && manualMonthlyFee >= 0) {
      // Calculate backwards from restaurant subscription
      restaurantSubscriptionMonthly = Math.max(0, Math.min(manualMonthlyFee, requiredTotalContribution));
      customerContributionMonthly = requiredTotalContribution - restaurantSubscriptionMonthly;
      baseCustomerFeePerOrder = customerContributionMonthly / safeMonthlyOrders;
      customerFeePerOrder = baseCustomerFeePerOrder * (1 + CUSTOMER_FEE_SAFETY_MARGIN);
      calculatedShare = clamp(customerContributionMonthly / requiredTotalContribution, 0, 1);
    }
    // Normal forward calculation
    else {
      // Split between customers and restaurant subscription using the slider
      customerContributionMonthly = requiredTotalContribution * safeShare;
      restaurantSubscriptionMonthly = requiredTotalContribution * (1 - safeShare);

      // Fee per order (customer side) with safety margin applied
      baseCustomerFeePerOrder = customerContributionMonthly / safeMonthlyOrders;
      customerFeePerOrder = baseCustomerFeePerOrder * (1 + CUSTOMER_FEE_SAFETY_MARGIN);
      calculatedShare = safeShare;
    }

    return {
      requiredTotalContribution,
      customerContributionMonthly,
      restaurantSubscriptionMonthly,
      baseCustomerFeePerOrder,
      customerFeePerOrder,
      calculatedShare,
      variableCostPerOrder,
      conversionRate: safeMonthlyCalls > 0 ? safeMonthlyOrders / safeMonthlyCalls : 0,
    };
  }, [safeMonthlyCalls, safeMonthlyOrders, safeShare, manualCustomerFee, manualMonthlyFee, variableCostPerOrder]);

  return (
    <div className="min-h-screen bg-neutral-50 p-6">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold">Pricing dial calculator</h1>
          <p className="mt-2 text-sm text-neutral-600">
            Enter the restaurant's key figures and choose how much should be paid by customers (service fee) vs. by
            the restaurant (monthly price). Customer fee automatically includes {Math.round(CUSTOMER_FEE_SAFETY_MARGIN * 100)}%
            safety margin. Costs account for calls that don't result in orders.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-medium text-neutral-700">Calls per month</span>
              <input
                type="number"
                min={MIN_CALLS_PER_MONTH}
                step={1}
                value={monthlyCalls}
                onChange={(e) => setMonthlyCalls(parseInt(e.target.value || "0", 10))}
                className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-neutral-400 focus:outline-none"
              />
              <div className="mt-1 text-xs text-neutral-500">Total phone calls received per month.</div>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-neutral-700">Orders per month</span>
              <input
                type="number"
                min={MIN_ORDERS_PER_MONTH}
                step={1}
                value={monthlyOrders}
                onChange={(e) => setMonthlyOrders(parseInt(e.target.value || "0", 10))}
                className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-neutral-900 shadow-sm focus:border-neutral-400 focus:outline-none"
              />
              <div className="mt-1 text-xs text-neutral-500">
                Orders completed per month. 
                {results.conversionRate > 0 && ` Conversion rate: ${Math.round(results.conversionRate * 100)}%`}
              </div>
            </label>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
              <div className="text-sm text-neutral-600">Monthly fee for restaurant</div>
              <input
                type="number"
                min={0}
                step={1}
                value={manualMonthlyFee !== null ? manualMonthlyFee : Math.round(results.restaurantSubscriptionMonthly)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (e.target.value === "" || isNaN(value) || value < 0) {
                    setManualMonthlyFee(null);
                    setManualCustomerFee(null);
                  } else {
                    setManualMonthlyFee(value);
                    setManualCustomerFee(null);
                  }
                }}
                onBlur={(e) => {
                  // Sync slider when manual value is set
                  if (manualMonthlyFee !== null && manualMonthlyFee >= 0) {
                    setCustomerFeeShare(results.calculatedShare);
                  } else if (e.target.value === "" || parseFloat(e.target.value) < 0) {
                    // Reset to calculated value if invalid
                    setManualMonthlyFee(null);
                  }
                }}
                className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-2xl font-semibold text-neutral-900 shadow-sm focus:border-neutral-400 focus:outline-none"
              />
              <div className="mt-2 text-xs text-neutral-500">
                {manualMonthlyFee !== null 
                  ? "Type to adjust. Other values will update automatically."
                  : `Based on ${safeMonthlyOrders.toLocaleString("en-US")} orders/month and your fixed costs/goals.`}
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
              <div className="text-sm text-neutral-600">Customer fee per order (average)</div>
              <input
                type="number"
                min={0}
                step={0.01}
                value={manualCustomerFee !== null ? manualCustomerFee.toFixed(2) : results.customerFeePerOrder.toFixed(2)}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (e.target.value === "" || isNaN(value) || value < 0) {
                    setManualCustomerFee(null);
                    setManualMonthlyFee(null);
                  } else {
                    setManualCustomerFee(value);
                    setManualMonthlyFee(null);
                  }
                }}
                onBlur={(e) => {
                  // Sync slider when manual value is set
                  if (manualCustomerFee !== null && manualCustomerFee >= 0) {
                    setCustomerFeeShare(results.calculatedShare);
                  } else if (e.target.value === "" || parseFloat(e.target.value) < 0) {
                    // Reset to calculated value if invalid
                    setManualCustomerFee(null);
                  }
                }}
                className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-2xl font-semibold text-neutral-900 shadow-sm focus:border-neutral-400 focus:outline-none"
              />
              <div className="mt-2 text-xs text-neutral-500">
                {manualCustomerFee !== null 
                  ? "Type to adjust. Other values will update automatically."
                  : `Including ${Math.round(CUSTOMER_FEE_SAFETY_MARGIN * 100)}% safety margin.`}
              </div>
              
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <div className="flex items-end justify-between gap-4 mb-2">
                  <div>
                    <div className="text-sm font-medium text-neutral-700">Customer fee share</div>
                    <div className="text-xs text-neutral-500">0% = restaurant pays all · 100% = customers pay all</div>
                  </div>
                  <div className="text-sm font-semibold">{Math.round(results.calculatedShare * 100)}%</div>
                </div>

                <input
                  type="range"
                  min={0}
                  max={100}
                  value={Math.round(results.calculatedShare * 100)}
                  onChange={(e) => {
                    const newShare = parseInt(e.target.value, 10) / 100;
                    setCustomerFeeShare(newShare);
                    // Clear manual values when slider is used
                    setManualCustomerFee(null);
                    setManualMonthlyFee(null);
                  }}
                  className="w-full"
                />

                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-neutral-500">
                  <div>Restaurant-heavy</div>
                  <div className="text-right">Customer-heavy</div>
                </div>
              </div>
            </div>
          </div>

          {/* <details className="mt-6 rounded-2xl border border-neutral-200 bg-white p-4">
            <summary className="cursor-pointer text-sm font-medium text-neutral-700">Show calculation details</summary>
            <div className="mt-3 grid gap-2 text-sm text-neutral-700">
              <div className="flex justify-between">
                <span>Orders/month</span>
                <span className="font-medium">{safeMonthlyOrders.toLocaleString("en-US")}</span>
              </div>
              <div className="flex justify-between">
                <span>Variable cost / order</span>
                <span className="font-medium">{formatDKK2(VARIABLE_COST_PER_ORDER_DKK)}</span>
              </div>
              <div className="flex justify-between">
                <span>Target profit / month</span>
                <span className="font-medium">{formatDKK(TARGET_PROFIT_PER_MONTH_DKK)}</span>
              </div>
              <div className="flex justify-between">
                <span>Required total contribution / month</span>
                <span className="font-medium">{formatDKK(results.requiredTotalContribution)}</span>
              </div>
              <div className="flex justify-between">
                <span>Customer contribution / month (before safety)</span>
                <span className="font-medium">{formatDKK(results.customerContributionMonthly)}</span>
              </div>
              <div className="flex justify-between">
                <span>Customer fee / order (before safety)</span>
                <span className="font-medium">{formatDKK2(results.baseCustomerFeePerOrder)}</span>
              </div>
              <div className="flex justify-between">
                <span>Customer fee / order (after safety)</span>
                <span className="font-medium">{formatDKK2(results.customerFeePerOrder)}</span>
              </div>
              <div className="flex justify-between">
                <span>Restaurant subscription / month</span>
                <span className="font-medium">{formatDKK(results.restaurantSubscriptionMonthly)}</span>
              </div>
              <div className="pt-2 text-xs text-neutral-500">
                Note: Average order value ({formatDKK(safeAvgOrderValue)}) is currently not used in the math. If you later want
                “5% of order value (min X)” pricing, we can add a second mode that derives the customer fee from order value.
              </div>
            </div>
          </details> */}
        </div>
      </div>
    </div>
  );
}
